import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Card, Form, Spinner, Badge, Table, Nav, Tab } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { colors } from '../../brand-colors';

/**
 * Unified Sales Page Generator
 * Used by both:
 * - /command-center/campaign-hq/sales-page
 * - /command-center/products/sales-pages
 *
 * Flow: Product Selection → Readiness Check → Pre-Gen Questions → Generate → Edit
 */
function UnifiedSalesPageGenerator() {
  const [searchParams] = useSearchParams();
  const preselectedProductId = searchParams.get('product');

  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('selection'); // selection, readiness, generating, editing
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState(null);
  const [siteConnected, setSiteConnected] = useState(false);

  // Products data
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [existingSalesPages, setExistingSalesPages] = useState({});

  // Content data
  const [salesPage, setSalesPage] = useState('');
  const [savedContent, setSavedContent] = useState(null);

  // Readiness check (product-aware)
  const [readiness, setReadiness] = useState({
    foundation: false,
    productUsp: false,
    productFeatures: false,
    irresistibleOffer: false,
    originStory: false,
    caseStudies: 0,
    missionVision: false,
    brandVoice: false,
    brandArchetype: false
  });

  // Pre-generation form data
  const [preGenData, setPreGenData] = useState({
    objections: '',
    customerCount: '',
    videoUrl: '',
    testimonialSource: 'skip',
    testimonialUrl: '',
    manualTestimonials: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  // If product pre-selected via URL param, auto-advance
  useEffect(() => {
    if (preselectedProductId && products.length > 0 && stage === 'selection') {
      const product = products.find(p => p.id === parseInt(preselectedProductId));
      if (product) {
        handleProductSelect(product);
      }
    }
  }, [preselectedProductId, products, stage]);

  const loadInitialData = async () => {
    try {
      // Fetch products
      const productsRes = await fetch('/api/products/get-catalog', { credentials: 'include' });
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.catalog || []);
      }

      // Fetch existing sales pages for all products
      const salesPagesRes = await fetch('/api/products/sales-pages', { credentials: 'include' });
      if (salesPagesRes.ok) {
        const data = await salesPagesRes.json();
        const pagesMap = {};
        (data.sales_pages || []).forEach(sp => {
          pagesMap[sp.product_id] = sp.content;
        });
        setExistingSalesPages(pagesMap);
      }

      // Check if WordPress site is connected
      const siteKeyRes = await fetch('/api/settings/site-key', { credentials: 'include' });
      if (siteKeyRes.ok) {
        const data = await siteKeyRes.json();
        setSiteConnected(!!data.site_url);
      }
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = async (product) => {
    setSelectedProduct(product);
    setLoading(true);

    // Reset pre-gen data before loading saved values
    setPreGenData({ objections: '', customerCount: '', videoUrl: '', testimonialSource: 'skip', testimonialUrl: '', manualTestimonials: '' });

    // Check if this product already has a sales page
    const existingContent = existingSalesPages[product.id];
    if (existingContent) {
      setSavedContent(existingContent);
      setSalesPage(existingContent);
    } else {
      setSavedContent(null);
      setSalesPage('');
    }

    // Load saved pre-gen answers for this product
    try {
      const preGenRes = await fetch(`/api/business/field/sales_page_pregen_${product.id}`, { credentials: 'include' });
      if (preGenRes.ok) {
        const preGenResult = await preGenRes.json();
        if (preGenResult.field_value) {
          const saved = JSON.parse(preGenResult.field_value);
          setPreGenData(prev => ({ ...prev, ...saved }));
        }
      }
    } catch (err) {
      // No saved pre-gen data, that's fine
    }

    // Load readiness data for this product
    await loadReadinessData(product);

    setStage('readiness');
    setLoading(false);
  };

  const loadReadinessData = async (product) => {
    // Check global brand assets
    let onboardingUspComplete = false;
    try {
      const onboardingResponse = await fetch('/api/onboarding/status', { credentials: 'include' });
      if (onboardingResponse.ok) {
        const onboardingData = await onboardingResponse.json();
        const items = onboardingData.required || [];
        const findItem = (id) => items.find(item => item.id === id);

        onboardingUspComplete = findItem('usp')?.complete || false;

        setReadiness(prev => ({
          ...prev,
          foundation: findItem('target_market')?.complete && findItem('core_product')?.complete,
          irresistibleOffer: findItem('offer')?.complete || false,
          originStory: findItem('origin_story')?.complete || false,
          missionVision: findItem('mission_vision')?.complete || false,
          brandVoice: findItem('brand_voice')?.complete || false
        }));
      }
    } catch (err) {
      console.error('Error loading onboarding status:', err);
    }

    // Core product: USP from BusinessData (saved on /usp-creation) OR Product.usp
    // Other products: USP from Product table (saved on catalog page)
    const hasProductUsp = product.position === 'core'
      ? !!(product.usp || product.usp_promise || onboardingUspComplete)
      : !!(product.usp || product.usp_promise);

    // Core product: check ProductFeature table (rich features from competitive analysis)
    // Other products: check Product.features_benefits (entered on catalog page)
    let hasProductFeatures = false;
    if (product.position === 'core') {
      try {
        const fbResponse = await fetch('/api/competitive-analysis/get-features-benefits', { credentials: 'include' });
        if (fbResponse.ok) {
          const fbData = await fbResponse.json();
          hasProductFeatures = (fbData.features?.length || 0) + (fbData.benefits?.length || 0) > 0;
        }
      } catch (err) {
        console.error('Error checking features:', err);
      }
    } else {
      hasProductFeatures = !!(product.features_benefits && product.features_benefits !== '[]');
    }

    setReadiness(prev => ({
      ...prev,
      productUsp: hasProductUsp,
      productFeatures: hasProductFeatures
    }));

    // Check case studies
    try {
      const caseStudiesResponse = await fetch('/api/authority-engineer/case-studies', { credentials: 'include' });
      if (caseStudiesResponse.ok) {
        const csData = await caseStudiesResponse.json();
        setReadiness(prev => ({
          ...prev,
          caseStudies: csData.case_studies?.length || 0
        }));
      }
    } catch (err) {
      console.error('Error loading case studies:', err);
    }

    // Check brand archetype
    try {
      const archetypeResponse = await fetch('/api/brand/get-archetypes', { credentials: 'include' });
      if (archetypeResponse.ok) {
        const archetypeData = await archetypeResponse.json();
        const hasArchetype = archetypeData.primary && archetypeData.primary.name;
        setReadiness(prev => ({
          ...prev,
          brandArchetype: hasArchetype
        }));
      }
    } catch (err) {
      console.error('Error loading archetype status:', err);
    }
  };

  const handleGenerate = async () => {
    if (!selectedProduct) return;

    setGenerating(true);
    setError('');
    setStage('generating');

    try {
      const response = await fetch('/api/assets/sales-page/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product_id: selectedProduct.id,
          objections: preGenData.objections,
          customer_count: preGenData.customerCount,
          video_url: preGenData.videoUrl,
          testimonial_source: preGenData.testimonialSource,
          testimonial_url: preGenData.testimonialUrl,
          manual_testimonials: preGenData.manualTestimonials
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSalesPage(data.content);
        setStage('editing');
      } else {
        const errorData = await response.json();
        setError(`Failed to generate content: ${errorData.error || 'Unknown error'}`);
        setStage('readiness');
      }
    } catch (err) {
      setError('Error generating content: ' + err.message);
      setStage('readiness');
    }

    setGenerating(false);
  };

  const handleSave = async () => {
    if (!salesPage || !salesPage.trim() || !selectedProduct) {
      setError('Please enter your sales page content');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Save to product-specific sales pages endpoint
      const response = await fetch('/api/products/sales-pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product_id: selectedProduct.id,
          content: salesPage
        })
      });

      if (response.ok) {
        setSavedContent(salesPage);
        setExistingSalesPages(prev => ({
          ...prev,
          [selectedProduct.id]: salesPage
        }));
        setError('');
        alert('Sales page saved successfully!');
      } else {
        const errorData = await response.json();
        setError(`Failed to save sales page: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving sales page: ' + err.message);
    }

    setSaving(false);
  };

  const handlePublish = async () => {
    if (!salesPage || !salesPage.trim() || !selectedProduct) {
      setError('Please save your sales page content first');
      return;
    }

    // First save to make sure content is up to date
    setSaving(true);
    try {
      const saveResponse = await fetch('/api/products/sales-pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product_id: selectedProduct.id,
          content: salesPage
        })
      });

      if (!saveResponse.ok) {
        setError('Failed to save before publishing');
        setSaving(false);
        return;
      }
    } catch (err) {
      setError('Error saving before publish: ' + err.message);
      setSaving(false);
      return;
    }
    setSaving(false);

    // Now publish to WordPress
    setPublishing(true);
    setError('');
    setSuccess('');
    setPublishedUrl(null);

    try {
      const response = await fetch(`/api/publish/sales-page/${selectedProduct.id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setPublishedUrl(data.url);
        setSuccess(`Published successfully! View at: ${data.url}`);
        setTimeout(() => setSuccess(''), 10000);
      } else {
        setError(data.error || 'Failed to publish to WordPress');
      }
    } catch (err) {
      setError('Error publishing to WordPress: ' + err.message);
    } finally {
      setPublishing(false);
    }
  };

  const handlePreGenChange = (field, value) => {
    setPreGenData(prev => ({ ...prev, [field]: value }));
  };

  const savePreGenData = async () => {
    if (!selectedProduct) return;
    try {
      await fetch('/api/business/field', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          field_name: `sales_page_pregen_${selectedProduct.id}`,
          field_value: JSON.stringify(preGenData)
        })
      });
    } catch (err) {
      // Silent save
    }
  };

  const handlePreGenBlur = () => {
    // Small delay to ensure state is updated before saving
    setTimeout(() => savePreGenData(), 50);
  };

  const getPositionBadge = (position) => {
    const badges = {
      'entry': { bg: 'success', label: 'Entry/Tripwire' },
      'core': { bg: 'primary', label: 'Core Offer' },
      'upsell': { bg: 'info', label: 'Upsell' },
      'premium': { bg: 'warning', label: 'Premium' }
    };
    const badge = badges[position] || { bg: 'secondary', label: position || 'Unassigned' };
    return <Badge bg={badge.bg}>{badge.label}</Badge>;
  };

  const goBackToSelection = () => {
    setSelectedProduct(null);
    setSalesPage('');
    setSavedContent(null);
    setStage('selection');
  };

  if (loading && stage === 'selection') {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" style={{ color: colors.goldMid }} />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="mb-4">
        <h1>Sales Page Generator</h1>
        <p className="text-muted">
          Create conversion-optimized long-form sales pages for any product in your catalog.
        </p>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* STAGE 1: Product Selection */}
      {stage === 'selection' && (
        <>
          <Card className="shadow-sm mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h5 className="mb-0">Step 1: Select Product</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Choose which product you want to create a sales page for. Each product can have its own customized sales page.
              </p>

              {products.length === 0 ? (
                <Alert variant="warning">
                  <strong>No products found.</strong> Please add products to your catalog first.
                  <div className="mt-2">
                    <Link to="/command-center/products/catalog" className="btn btn-sm btn-outline-dark">
                      Go to Product Catalog
                    </Link>
                  </div>
                </Alert>
              ) : (
                <Table responsive hover>
                  <thead style={{ backgroundColor: colors.greyLightest }}>
                    <tr>
                      <th>Product</th>
                      <th>Position</th>
                      <th className="text-end">Price</th>
                      <th className="text-center">Sales Page</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => {
                      const hasExisting = !!existingSalesPages[product.id];
                      const isCore = product.position === 'core';

                      return (
                        <tr key={product.id} style={isCore ? { backgroundColor: '#fffde7' } : {}}>
                          <td>
                            <strong>{product.name}</strong>
                            {isCore && <Badge bg="warning" text="dark" className="ms-2">Core Product</Badge>}
                          </td>
                          <td>{getPositionBadge(product.position)}</td>
                          <td className="text-end">
                            ${product.sale_price?.toLocaleString() || '0'}
                            {product.purchase_type?.includes('recurring') && <span className="text-muted">/mo</span>}
                          </td>
                          <td className="text-center">
                            {hasExisting ? (
                              <Badge bg="success">Created</Badge>
                            ) : (
                              <Badge bg="secondary">Not Created</Badge>
                            )}
                          </td>
                          <td className="text-end">
                            <Button
                              variant={isCore ? 'warning' : 'outline-primary'}
                              size="sm"
                              onClick={() => handleProductSelect(product)}
                            >
                              {hasExisting ? 'Edit' : 'Create'} Sales Page
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Tips Card */}
          <Card className="shadow-sm" style={{ backgroundColor: colors.greyLightest }}>
            <Card.Body>
              <h6>Tips</h6>
              <ul className="small mb-0 text-muted">
                <li><strong>Core Product</strong> (highlighted) is your main offer - start here if you haven't already</li>
                <li>Each product can have its own unique sales page tailored to its audience</li>
                <li>Products with more details (USP, features, benefits) generate better sales pages</li>
              </ul>
            </Card.Body>
          </Card>
        </>
      )}

      {/* STAGE 2: Readiness Check - Continued in next chunk */}
      {stage === 'readiness' && selectedProduct && (
        <ReadinessStage
          selectedProduct={selectedProduct}
          readiness={readiness}
          preGenData={preGenData}
          handlePreGenChange={handlePreGenChange}
          handlePreGenBlur={handlePreGenBlur}
          handleGenerate={handleGenerate}
          generating={generating}
          savedContent={savedContent}
          goBackToSelection={goBackToSelection}
          setStage={setStage}
          existingSalesPages={existingSalesPages}
        />
      )}

      {/* STAGE 3: Generating */}
      {stage === 'generating' && (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" className="mb-3" style={{ color: colors.goldMid }} />
            <h4>Generating Sales Page for {selectedProduct?.name}...</h4>
            <p className="text-muted">
              This will take about 60-90 seconds. We're creating a comprehensive, conversion-optimized sales page.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* STAGE 4: Editing */}
      {stage === 'editing' && selectedProduct && (
        <EditingStage
          selectedProduct={selectedProduct}
          salesPage={salesPage}
          setSalesPage={setSalesPage}
          handleSave={handleSave}
          saving={saving}
          savedContent={savedContent}
          handleGenerate={handleGenerate}
          generating={generating}
          setStage={setStage}
          goBackToSelection={goBackToSelection}
          handlePublish={handlePublish}
          publishing={publishing}
          publishedUrl={publishedUrl}
          siteConnected={siteConnected}
          success={success}
        />
      )}
    </Container>
  );
}

// Readiness Stage Component
function ReadinessStage({
  selectedProduct,
  readiness,
  preGenData,
  handlePreGenChange,
  handlePreGenBlur,
  handleGenerate,
  generating,
  savedContent,
  goBackToSelection,
  setStage,
  existingSalesPages
}) {
  return (
    <Row>
      <Col lg={8}>
        {/* Product Info Banner */}
        <Alert variant="info" className="mb-4" style={{ backgroundColor: '#fff8e6', borderColor: colors.goldMid, color: colors.greyDarkest }}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Creating sales page for:</strong> {selectedProduct.name}
              <span className="ms-2">
                (${selectedProduct.sale_price?.toLocaleString() || '0'}
                {selectedProduct.purchase_type?.includes('recurring') ? '/mo' : ''})
              </span>
            </div>
            <Button variant="outline-secondary" size="sm" onClick={goBackToSelection}>
              Change Product
            </Button>
          </div>
        </Alert>

        {/* Conversion Boosters */}
        <Card className="shadow-sm mb-4">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h5 className="mb-0">Conversion Boosters</h5>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">
              Each element below makes your sales page more persuasive. Add what you can - we'll work with what you have.
            </p>

            <div className="mb-3">
              {/* Product-specific USP */}
              <ReadinessItem
                complete={readiness.productUsp}
                label={`${selectedProduct.name} USP`}
                completeText="Included"
                incompleteText="+25% conversion (differentiation)"
                linkTo={`/command-center/products/catalog?edit=${selectedProduct.id}`}
              />

              {/* Product-specific Features */}
              <ReadinessItem
                complete={readiness.productFeatures}
                label={`${selectedProduct.name} Features & Benefits`}
                completeText="Included"
                incompleteText="+20% conversion (value clarity)"
                linkTo={`/command-center/products/catalog?edit=${selectedProduct.id}`}
              />

              {/* Foundation */}
              <ReadinessItem
                complete={readiness.foundation}
                label="Target Market & Foundation"
                completeText="Included"
                incompleteText="+15% conversion (targeting)"
                linkTo="/target-market-discovery"
              />

              {/* Origin Story */}
              <ReadinessItem
                complete={readiness.originStory}
                label="Origin Story"
                completeText="Included"
                incompleteText="+40% conversion (emotional connection)"
                linkTo="/command-center/origin-story"
              />

              {/* Case Studies */}
              <ReadinessItem
                complete={readiness.caseStudies > 0}
                label={`Case Studies${readiness.caseStudies > 0 ? ` (${readiness.caseStudies})` : ''}`}
                completeText={`${readiness.caseStudies} included`}
                incompleteText="+35% conversion (social proof)"
                linkTo="/command-center/case-study"
              />

              {/* Mission & Vision */}
              <ReadinessItem
                complete={readiness.missionVision}
                label="Mission & Vision"
                completeText="Included"
                incompleteText="+15% conversion (purpose)"
                linkTo="/mission"
              />

              {/* Brand Voice */}
              <ReadinessItem
                complete={readiness.brandVoice}
                label="Brand Voice"
                completeText="Included"
                incompleteText="+20% conversion (consistency)"
                linkTo="/brand-voice-analysis"
              />

              {/* Brand Archetype */}
              <ReadinessItem
                complete={readiness.brandArchetype}
                label="Brand Archetype"
                completeText="Included"
                incompleteText="+25% conversion (personality)"
                linkTo="/brand-archetype"
                noBorder
              />
            </div>

            <Alert variant="light" className="mb-0 small border">
              <strong>Tip:</strong> You can generate now and add more elements later. Each addition makes your page stronger.
            </Alert>
          </Card.Body>
        </Card>

        {/* Pre-Generation Questions */}
        <Card className="shadow-sm mb-4">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h5 className="mb-0">Quick Questions</h5>
          </Card.Header>
          <Card.Body>
            <Form>
              <Form.Group className="mb-4">
                <Form.Label><strong>What makes prospects hesitate before buying {selectedProduct.name}?</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="List 2-3 common objections or concerns..."
                  value={preGenData.objections}
                  onChange={(e) => handlePreGenChange('objections', e.target.value)}
                  onBlur={handlePreGenBlur}
                />
                <Form.Text className="text-muted">
                  We'll address these directly in your FAQ and throughout the page.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label><strong>Approximately how many customers have purchased this product?</strong></Form.Label>
                <Form.Select
                  value={preGenData.customerCount}
                  onChange={(e) => { handlePreGenChange('customerCount', e.target.value); setTimeout(handlePreGenBlur, 100); }}
                >
                  <option value="">Select...</option>
                  <option value="starting">Just starting out</option>
                  <option value="under50">Less than 50</option>
                  <option value="50-100">50-100</option>
                  <option value="100-500">100-500</option>
                  <option value="500+">500+</option>
                  <option value="skip">I'd rather not say</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  We'll use appropriate social proof language based on your experience.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label><strong>Do you have a sales video for this product?</strong> (optional)</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://vimeo.com/... or https://youtube.com/..."
                  value={preGenData.videoUrl}
                  onChange={(e) => handlePreGenChange('videoUrl', e.target.value)}
                  onBlur={handlePreGenBlur}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label><strong>Testimonials</strong></Form.Label>
                <Form.Select
                  value={preGenData.testimonialSource}
                  onChange={(e) => { handlePreGenChange('testimonialSource', e.target.value); setTimeout(handlePreGenBlur, 100); }}
                  className="mb-2"
                >
                  <option value="skip">Skip for now (use placeholders)</option>
                  <option value="caseStudies">Use my Case Studies</option>
                  <option value="manual">I'll enter manually</option>
                </Form.Select>

                {preGenData.testimonialSource === 'manual' && (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Paste your testimonials here (include name, title if available)..."
                    value={preGenData.manualTestimonials}
                    onChange={(e) => handlePreGenChange('manualTestimonials', e.target.value)}
                    onBlur={handlePreGenBlur}
                    className="mt-2"
                  />
                )}
              </Form.Group>
            </Form>

            <div className="d-grid">
              <Button
                size="lg"
                onClick={handleGenerate}
                disabled={generating}
                style={{ backgroundColor: colors.goldDark, borderColor: colors.goldDark, color: '#ffffff' }}
              >
                Generate Sales Page for {selectedProduct.name}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col lg={4}>
        <Card className="shadow-sm" style={{ backgroundColor: colors.greyLightest }}>
          <Card.Body>
            <h5 className="mb-3" style={{ color: colors.greyDarkest }}>What We'll Include</h5>
            <ul className="small mb-0" style={{ color: colors.greyDark }}>
              <li>Pre-headline & attention-grabbing headline</li>
              <li>Your origin story (if completed)</li>
              <li>Pain agitation & enemy identification</li>
              <li>Your unique mechanism/approach</li>
              <li>All features with emotional benefits</li>
              <li>Social proof & testimonials</li>
              <li>Value stack & pricing</li>
              <li>Your guarantee & risk reversal</li>
              <li>FAQ addressing objections</li>
              <li>Multiple CTAs throughout</li>
            </ul>
          </Card.Body>
        </Card>

        {existingSalesPages[selectedProduct?.id] && (
          <Card className="shadow-sm mt-3" style={{ backgroundColor: '#e8f5e9' }}>
            <Card.Body>
              <h6 className="mb-2">You have a saved sales page</h6>
              <p className="small mb-2">Generating a new one will replace your current version.</p>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setStage('editing')}
              >
                View/Edit Current Page
              </Button>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
}

// Readiness Item Component
function ReadinessItem({ complete, label, completeText, incompleteText, linkTo, noBorder }) {
  return (
    <div className={`d-flex align-items-center justify-content-between py-2 ${noBorder ? '' : 'border-bottom'}`}>
      <div className="d-flex align-items-center">
        {complete ? (
          <Badge bg="success" className="me-2">✓</Badge>
        ) : (
          <Badge bg="light" text="dark" className="me-2">○</Badge>
        )}
        <span>{label}</span>
      </div>
      {complete ? (
        <span className="text-success small">{completeText}</span>
      ) : (
        <Link to={linkTo} className="small text-decoration-none">
          {incompleteText} →
        </Link>
      )}
    </div>
  );
}

// Editing Stage Component
function EditingStage({
  selectedProduct,
  salesPage,
  setSalesPage,
  handleSave,
  saving,
  savedContent,
  handleGenerate,
  generating,
  setStage,
  goBackToSelection,
  handlePublish,
  publishing,
  publishedUrl,
  siteConnected,
  success
}) {
  return (
    <>
      <Card className="shadow-sm mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <h5 className="mb-0">Sales Page: {selectedProduct.name}</h5>
          <div>
            <Button
              variant="outline-light"
              size="sm"
              onClick={goBackToSelection}
              className="me-2"
            >
              ← Change Product
            </Button>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setStage('readiness')}
              className="me-2"
            >
              Back to Setup
            </Button>
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Regenerating...
                </>
              ) : (
                'Regenerate'
              )}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-4" style={{ backgroundColor: '#fff8e6', borderColor: colors.goldMid, color: colors.greyDarkest }}>
            <strong>Review your AI-generated sales page below.</strong> Edit to customize, add real testimonials, and verify all details are accurate.
          </Alert>

          <Tab.Container defaultActiveKey="preview">
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="preview" style={{ color: colors.greyDarkest, fontWeight: '600' }}>
                  Preview
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="edit" style={{ color: colors.greyDarkest, fontWeight: '600' }}>
                  Edit Source
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="preview">
                <div
                  className="sales-page-preview border rounded"
                  style={{
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <ReactMarkdown
                    components={{
                      h1: ({children}) => (
                        <div style={{ textAlign: 'center', padding: '3rem 2rem 2rem', background: `linear-gradient(180deg, ${colors.greyDarkest} 0%, #1a1a1a 100%)`, color: '#ffffff', marginBottom: '2rem' }}>
                          <h1 style={{ fontSize: '2.6rem', fontWeight: '700', lineHeight: '1.2', marginBottom: '0', fontFamily: "'Georgia', 'Times New Roman', serif", maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>{children}</h1>
                        </div>
                      ),
                      h2: ({children}) => (
                        <h2 style={{ fontSize: '1.8rem', fontWeight: '700', marginTop: '2.5rem', marginBottom: '1rem', color: colors.greyDarkest, padding: '0 2rem', textAlign: 'center' }}>{children}</h2>
                      ),
                      h3: ({children}) => (
                        <h3 style={{ fontSize: '1.4rem', fontWeight: '600', marginTop: '2rem', marginBottom: '0.8rem', color: colors.goldDark, padding: '0 2rem' }}>{children}</h3>
                      ),
                      p: ({children}) => (
                        <p style={{ marginBottom: '1.1rem', lineHeight: '1.8', fontSize: '16px', padding: '0 2rem', color: '#333' }}>{children}</p>
                      ),
                      strong: ({children}) => <strong style={{ color: colors.greyDarkest }}>{children}</strong>,
                      em: ({children}) => <em style={{ color: '#555' }}>{children}</em>,
                      ul: ({children}) => <ul style={{ paddingLeft: '3.5rem', paddingRight: '2rem', marginBottom: '1.2rem' }}>{children}</ul>,
                      ol: ({children}) => <ol style={{ paddingLeft: '3.5rem', paddingRight: '2rem', marginBottom: '1.2rem' }}>{children}</ol>,
                      li: ({children}) => <li style={{ marginBottom: '0.5rem', lineHeight: '1.7', fontSize: '16px', color: '#333' }}>{children}</li>,
                      hr: () => (
                        <div style={{ textAlign: 'center', margin: '2.5rem 2rem' }}>
                          <div style={{ width: '80px', height: '3px', background: `linear-gradient(90deg, ${colors.goldLight}, ${colors.goldDark})`, margin: '0 auto' }} />
                        </div>
                      ),
                      blockquote: ({children}) => (
                        <div style={{ margin: '1.5rem 2rem', padding: '1.2rem 1.5rem', backgroundColor: '#f9f7f2', borderLeft: `4px solid ${colors.goldMid}`, borderRadius: '0 8px 8px 0' }}>
                          <div style={{ fontStyle: 'italic', fontSize: '17px', lineHeight: '1.7', color: '#444' }}>{children}</div>
                        </div>
                      ),
                      a: ({children, href}) => (
                        <span style={{ display: 'inline-block', padding: '0.5rem 1.5rem', background: `linear-gradient(90deg, #D03737, #A30202)`, color: '#ffffff', borderRadius: '6px', fontWeight: '600', textDecoration: 'none', cursor: 'pointer' }}>{children}</span>
                      )
                    }}
                  >
                    {salesPage || ''}
                  </ReactMarkdown>
                </div>
              </Tab.Pane>
              <Tab.Pane eventKey="edit">
                <Form.Group className="mb-3">
                  <Form.Control
                    as="textarea"
                    rows={30}
                    value={salesPage || ''}
                    onChange={(e) => setSalesPage(e.target.value)}
                    style={{ fontFamily: 'monospace', fontSize: '14px' }}
                  />
                  <Form.Text className="text-muted">
                    Edit the markdown source. Switch to Preview to see how it looks.
                  </Form.Text>
                </Form.Group>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>

          <div className="d-flex gap-2">
            <Button
              size="lg"
              onClick={handleSave}
              disabled={saving || publishing || !salesPage || !salesPage.trim()}
              style={{ backgroundColor: colors.goldDark, borderColor: colors.goldDark, color: '#ffffff' }}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Sales Page'
              )}
            </Button>

            {siteConnected && (
              <Button
                size="lg"
                variant="success"
                onClick={handlePublish}
                disabled={publishing || saving || !salesPage || !salesPage.trim()}
                title={!siteConnected ? 'Connect WordPress site first' : 'Save and publish to WordPress'}
              >
                {publishing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Publishing...
                  </>
                ) : publishedUrl ? (
                  'Republish to WordPress'
                ) : (
                  'Publish to WordPress'
                )}
              </Button>
            )}
          </div>

          {!siteConnected && (
            <Alert variant="info" className="mt-3 mb-0">
              <small>Connect your WordPress site in Onboarding to enable publishing.</small>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {success && (
        <Alert variant="success">
          <strong>Published!</strong><br />
          {success}
          {publishedUrl && (
            <div className="mt-2">
              <a href={publishedUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success">
                View Live Page
              </a>
            </div>
          )}
        </Alert>
      )}

      {savedContent && !success && (
        <Alert variant="info">
          <strong>Sales Page Saved</strong><br />
          Your sales page for {selectedProduct.name} is saved. You can make additional edits anytime.
          {siteConnected && !publishedUrl && (
            <span> Click "Publish to WordPress" to make it live.</span>
          )}
        </Alert>
      )}
    </>
  );
}

export default UnifiedSalesPageGenerator;
